/* =========================================================
   CQL PRODUCT ADD-ONS
   Shared behavior for cql.addon_product.

   If no add-ons are selected, the theme's normal Add To Cart behavior
   is left completely untouched.

   If one or more add-ons are selected, this script intercepts the
   existing product form submission and sends the main PDP variant plus
   every checked add-on variant to Shopify in one /cart/add.js request.
   ========================================================= */

(function () {
  'use strict';

  function initAll() {
    document.querySelectorAll('[data-cql-product-addons]').forEach(initAddons);
  }

  function initAddons(root) {
    if (!root || root.dataset.cqlAddonsInitialized === 'true') {
      return;
    }

    var form = root.closest('form');

    if (!form) {
      form = document.getElementById('AddToCartForm');
    }

    if (!form) {
      return;
    }

    var checkboxes = Array.prototype.slice.call(
      root.querySelectorAll('[data-cql-addon-checkbox]')
    );

    if (!checkboxes.length) {
      return;
    }

    var variantsScript = root.querySelector('[data-cql-addon-main-variants]');
    var variants = [];

    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent || '[]');
      } catch (error) {
        variants = [];
      }
    }

    var hideMainPrice = root.getAttribute('data-cql-hide-main-price') === 'true';
    var totalElement = root.querySelector('[data-cql-addon-total]');
    var totalCompareElement = root.querySelector('[data-cql-addon-total-compare]');
    var errorElement = root.querySelector('[data-cql-addon-error]');
    var variantInput = form.querySelector('[name="id"]');
    var quantityInput = form.querySelector('[name="quantity"]');
    var addButton = form.querySelector('#AddToCart, button[name="add"], .product-form__cart-submit');
    var submitting = false;

    root.dataset.cqlAddonsInitialized = 'true';

    checkboxes.forEach(function (checkbox) {
      updateCardSelection(checkbox);

      checkbox.addEventListener('change', function () {
        updateCardSelection(checkbox);
        clearError();
        updateTotal();
      });
    });

    if (variantInput) {
      variantInput.addEventListener('change', function () {
        clearError();
        updateTotal();
      });
    }

    if (quantityInput) {
      quantityInput.addEventListener('input', updateTotal);
      quantityInput.addEventListener('change', updateTotal);
    }

    /*
     * Capture phase is deliberate. Gramophone's theme may also bind a
     * submit listener to #AddToCartForm for its Ajax Cart. When add-ons
     * are checked, our multi-item request must run instead so the main
     * product is not submitted twice.
     */
    form.addEventListener(
      'submit',
      function (event) {
        if (!hasSelectedAddons()) {
          return;
        }

        /* Preserve cql.web_permissions FIND IN STORE behavior. */
        if (
          document.body.classList.contains('cql-web-in-store-only') ||
          (addButton && addButton.classList.contains('cql-find-in-store'))
        ) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        if (!submitting) {
          addSelectedItems();
        }
      },
      true
    );

    updateTotal();

    function updateCardSelection(checkbox) {
      var card = checkbox.closest('[data-cql-addon-card]');

      if (card) {
        card.classList.toggle('is-selected', checkbox.checked && !checkbox.disabled);
      }
    }

    function hasSelectedAddons() {
      return checkboxes.some(function (checkbox) {
        return checkbox.checked && !checkbox.disabled;
      });
    }

    function getCurrentVariant() {
      var selectedId = variantInput ? String(variantInput.value || '') : '';

      if (!selectedId && variants.length) {
        selectedId = String(variants[0].id);
      }

      for (var i = 0; i < variants.length; i += 1) {
        if (String(variants[i].id) === selectedId) {
          return variants[i];
        }
      }

      return variants.length ? variants[0] : null;
    }

    function getMainQuantity() {
      var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

      if (!quantity || quantity < 1) {
        quantity = 1;
      }

      return quantity;
    }

    function updateTotal() {
      if (hideMainPrice) {
        if (totalElement) {
          totalElement.textContent = 'See Price in Cart';
        }
        if (totalCompareElement) {
          totalCompareElement.textContent = '';
          totalCompareElement.hidden = true;
        }
        return;
      }

      var mainVariant = getCurrentVariant();
      var mainQuantity = getMainQuantity();
      var total = mainVariant ? Number(mainVariant.price || 0) * mainQuantity : 0;
      var mainCompare = mainVariant ? Number(mainVariant.compare_at_price || 0) : 0;
      var compareTotal = Math.max(mainCompare, mainVariant ? Number(mainVariant.price || 0) : 0) * mainQuantity;

      checkboxes.forEach(function (checkbox) {
        if (!checkbox.checked || checkbox.disabled) {
          return;
        }

        var price = Number(checkbox.getAttribute('data-price') || 0);
        var compare = Number(checkbox.getAttribute('data-compare-price') || 0);

        total += price;
        compareTotal += Math.max(compare, price);
      });

      if (totalElement) {
        totalElement.textContent = formatMoney(total, root.getAttribute('data-money-format'));
      }

      if (totalCompareElement) {
        if (compareTotal > total) {
          totalCompareElement.textContent = formatMoney(
            compareTotal,
            root.getAttribute('data-money-format')
          );
          totalCompareElement.hidden = false;
        } else {
          totalCompareElement.textContent = '';
          totalCompareElement.hidden = true;
        }
      }
    }

    function buildItems() {
      var currentVariant = getCurrentVariant();
      var selectedVariantId = variantInput ? variantInput.value : currentVariant && currentVariant.id;
      var itemsByVariant = {};

      if (!selectedVariantId) {
        throw new Error('Please select an available product option.');
      }

      addItem(itemsByVariant, selectedVariantId, getMainQuantity());

      checkboxes.forEach(function (checkbox) {
        if (!checkbox.checked || checkbox.disabled) {
          return;
        }

        addItem(itemsByVariant, checkbox.getAttribute('data-variant-id'), 1);
      });

      return Object.keys(itemsByVariant).map(function (variantId) {
        return {
          id: Number(variantId),
          quantity: itemsByVariant[variantId]
        };
      });
    }

    function addItem(itemsByVariant, variantId, quantity) {
      if (!variantId) {
        return;
      }

      var key = String(variantId);
      var parsedQuantity = parseInt(quantity, 10) || 1;

      if (!itemsByVariant[key]) {
        itemsByVariant[key] = 0;
      }

      itemsByVariant[key] += parsedQuantity;
    }

    function addSelectedItems() {
      var items;

      try {
        items = buildItems();
      } catch (error) {
        showError(error.message || 'Unable to add the selected products to the cart.');
        return;
      }

      if (items.length < 2) {
        /*
         * This should only happen if selections changed between submit and
         * execution. Fall back to the theme's normal submission rather than
         * making a multi-item request unnecessarily.
         */
        return;
      }

      submitting = true;
      clearError();
      setLoading(true);

      var rootPath =
        window.Shopify && Shopify.routes && Shopify.routes.root
          ? Shopify.routes.root
          : '/';

      fetch(rootPath + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (response.ok) {
            return response.json();
          }

          return response.json().then(function (data) {
            throw new Error(
              data.description ||
                data.message ||
                'Unable to add the selected products to the cart.'
            );
          });
        })
        .then(function (data) {
          notifyThemeSuccess(data);
        })
        .catch(function (error) {
          showError(error.message || 'Unable to add the selected products to the cart.');
        })
        .then(function () {
          submitting = false;
          setLoading(false);
        });
    }

    function notifyThemeSuccess(data) {
      /*
       * Reuse Gramophone's existing Ajax Cart success UI when available.
       * This keeps the experience consistent with a normal PDP add-to-cart.
       */
      if (
        window.theme &&
        theme.AjaxCart &&
        typeof theme.AjaxCart.notify === 'function'
      ) {
        theme.AjaxCart.notify('success', false, true);

        document.querySelectorAll('.site-header__cart-indicator').forEach(function (indicator) {
          indicator.classList.remove('hide');
        });

        return;
      }

      /* Fallback if the theme notification object is unavailable. */
      var rootPath =
        window.Shopify && Shopify.routes && Shopify.routes.root
          ? Shopify.routes.root
          : '/';

      window.location.href = rootPath + 'cart';
    }

    function setLoading(isLoading) {
      root.classList.toggle('is-loading', isLoading);
      root.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      if (addButton) {
        addButton.disabled = isLoading;
        addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }

    function showError(message) {
      if (!errorElement) {
        return;
      }

      errorElement.textContent = message;
      errorElement.hidden = false;
    }

    function clearError() {
      if (!errorElement) {
        return;
      }

      errorElement.textContent = '';
      errorElement.hidden = true;
    }
  }

  function formatMoney(cents, moneyFormat) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(
        cents,
        (window.theme && theme.moneyFormat) || moneyFormat || '${{amount}}'
      );
    }

    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
